import { useMutation, useQuery } from '@apollo/client';
import { useCallback, useMemo, useState } from 'react';
import useFeatureFlag, { FEATURE_FLAGS } from 'flags/useFeatureFlag';
import { logException } from 'App/logging';
import { WelcomeStatus } from '__generated__/globalTypes';
import { OnboardingSupportOptions } from './graphql/__generated__/OnboardingSupportOptions';
import {
  SetWelcomeModalStatus,
  SetWelcomeModalStatusVariables,
} from './graphql/__generated__/SetWelcomeModalStatus';
import { WelcomeModalStatus } from './graphql/__generated__/WelcomeModalStatus';
import { ONBOARDING_SUPPORT_OPTIONS_QUERY } from './graphql/onboardingSupportOptions';
import {
  SET_WELCOME_MODAL_STATUS_MUTATION,
  WELCOME_MODAL_STATUS_QUERY,
} from './graphql/welcomeModalStatus';
import {
  getDescribableOptions,
  MINIMUM_SUPPORT_OPTIONS,
} from './supportOptions';

/**
 * Decides whether to show the welcome modal, and records the outcome.
 *
 * `pending` is the whole eligibility test: it is written server-side at account
 * creation, so accounts predating the modal have no value and are never asked,
 * and acting on it records an outcome that never returns to pending.
 *
 * The checks cascade so nothing is fetched before it is needed, and every
 * failure resolves to "do not show" — showing it twice is worse than not at all.
 */
export const useWelcomeModal = () => {
  const [dismissed, setDismissed] = useState(false);

  const isActive = Boolean(useFeatureFlag(FEATURE_FLAGS.WELCOME_MODAL_ENABLED));

  const { data: statusData, error: statusError } = useQuery<WelcomeModalStatus>(
    WELCOME_MODAL_STATUS_QUERY,
    {
      skip: !isActive,
    },
  );

  const isPending =
    !statusError &&
    statusData?.user?.onboarding.welcomeStatus === WelcomeStatus.pending;

  const { data: optionsData, error: optionsError } =
    useQuery<OnboardingSupportOptions>(ONBOARDING_SUPPORT_OPTIONS_QUERY, {
      skip: !isActive || !isPending,
      onError: error =>
        logException(error, {
          extra: { reason: "Could not read the member's support options" },
        }),
    });

  const [setStatus] = useMutation<
    SetWelcomeModalStatus,
    SetWelcomeModalStatusVariables
  >(SET_WELCOME_MODAL_STATUS_MUTATION, {
    // The mutation returns only a boolean, and watch queries default to
    // cache-and-network, so without this the next homepage visit reads the
    // stale `pending` from cache and flashes the modal before the server
    // answers. Write the new status in rather than refetching.
    update: (cache, result, { variables }) => {
      const welcomeStatus = variables?.userInput.welcomeStatus;

      // `updateUser` reports success without rejecting, so a false or null
      // result has to be checked rather than caught.
      if (!welcomeStatus || !result.data?.updateUser) {
        return;
      }

      cache.updateQuery<WelcomeModalStatus>(
        { query: WELCOME_MODAL_STATUS_QUERY },
        existing =>
          existing?.user
            ? {
                ...existing,
                user: {
                  ...existing.user,
                  onboarding: { ...existing.user.onboarding, welcomeStatus },
                },
              }
            : existing,
      );
    },
  });

  // Closes either way: a failed write means the member may be asked again,
  // which beats trapping them behind a modal that will not close.
  const recordOutcome = useCallback(
    (welcomeStatus: WelcomeStatus) => {
      setDismissed(true);
      void setStatus({ variables: { userInput: { welcomeStatus } } }).catch(
        error =>
          logException(error, {
            extra: {
              reason: 'Could not record the welcome modal outcome',
              welcomeStatus,
            },
          }),
      );
    },
    [setStatus],
  );

  const supportOptions = optionsData?.onboardingSupportOptions;

  const options = useMemo(
    () => getDescribableOptions(supportOptions?.options ?? []),
    [supportOptions],
  );

  return {
    isOpen:
      isActive &&
      isPending &&
      !dismissed &&
      !optionsError &&
      options.length >= MINIMUM_SUPPORT_OPTIONS,
    firstName: statusData?.user?.firstName,
    options,
    practicalSupport: supportOptions?.practicalSupport ?? null,
    complete: useCallback(
      () => recordOutcome(WelcomeStatus.completed),
      [recordOutcome],
    ),
    dismiss: useCallback(
      () => recordOutcome(WelcomeStatus.dismissed),
      [recordOutcome],
    ),
  };
};
